(function () {
    // Flip to false to disable all Shopify Forms shadow-DOM styling
    // (reverts forms to their default Shopify-rendered look).
    const ENABLED = true;

    // Flip to false to disable just the dropdown label-as-placeholder
    // behavior (external <p> labels stay visible, no in-button placeholder,
    // no floating-caption-on-select). Other form styling remains in effect.
    const DROPDOWN_LABELS = false;

    if (!ENABLED) return;

    const STYLE_ID = 'akin-form-shadow-styles';
    const injected = new WeakSet();

    // CSS is inlined here (not fetched) so it works reliably across the
    // Shopify CDN cache and avoids any cross-origin fetch quirks.
    const BASE_CSS = `
    :host {
      --form-field-border: 0 0 0 1px #000000 !important;
      --form-field-border-focused: 0 0 0 1px #000000 !important;
      --form-field-border-radius: 0 !important;
      --button-border-radius: 0 !important;
      --container-border-radius: 0 !important;
      --form-placeholder-color: #000000 !important;
      --forms-accent-color: #000000 !important;
      --forms-accent-color-hovered-modifier: rgba(0, 0, 0, 0.06) !important;
      --link-text-color: #000000 !important;
      --forms-discount-code-color: #000000 !important;
    }

    button[class*="_selectToggle_"] {
      border-radius: 0 !important;
      border: none !important;
      box-shadow: 0 0 0 1px #000000 !important;
      outline: none !important;
    }
    button[class*="_selectToggle_"]:focus,
    button[class*="_selectToggle_"]:focus-visible,
    button[class*="_selectToggle_"][aria-expanded="true"] {
      box-shadow: 0 0 0 1px #000000 !important;
      outline: none !important;
    }

    input:focus,
    input:focus-visible,
    textarea:focus,
    textarea:focus-visible,
    button:focus,
    button:focus-visible {
      outline: none !important;
    }

    button[type="submit"] {
      border-radius: 0 !important;
      text-transform: none !important;
      letter-spacing: normal !important;
      font-weight: 400 !important;
    }

    /* Field labels: text-input floating labels and dropdown <p> labels */
    [class*="_formInputFieldLabel_"] {
      font-weight: 400 !important;
    }
    [class*="_selectContainer_"] > [class*="_label_"] {
      font-size: 12px !important;
      font-weight: 600 !important;
    }
  `;

    // Dropdown fields: while no value is selected, hide the external <p>
    // label and render its text as a placeholder inside the toggle button
    // (matches text-input UX). Once a value is selected, the <p> label
    // reappears as a small caption above the dropdown so the user can
    // still see which question they answered.
    const DROPDOWN_LABELS_CSS = `
    [class*="_selectContainer_"] > [class*="_label_"] {
      display: none !important;
    }
    [class*="_selectToggleText_"]:empty::before {
      content: attr(data-placeholder);
      color: #000000;
    }
    [class*="_selectContainer_"]:has([class*="_selectToggleText_"]:not(:empty)) > [class*="_label_"] {
      display: block !important;
      margin: 0 0 4px 0 !important;
      font-size: 11px !important;
      text-align: left !important;
      color: #000000 !important;
      text-transform: uppercase !important;
      letter-spacing: 1px !important;
      opacity: 1 !important;
    }
  `;

    const SHADOW_CSS = BASE_CSS + (DROPDOWN_LABELS ? DROPDOWN_LABELS_CSS : '');

    function inject(shadowRoot) {
        if (!shadowRoot || injected.has(shadowRoot)) return;
        if (shadowRoot.getElementById && shadowRoot.getElementById(STYLE_ID)) {
            injected.add(shadowRoot);
            if (DROPDOWN_LABELS) setupDropdownPlaceholders(shadowRoot);
            return;
        }
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = SHADOW_CSS;
        shadowRoot.appendChild(style);
        injected.add(shadowRoot);
        if (DROPDOWN_LABELS) setupDropdownPlaceholders(shadowRoot);
    }

    function setupDropdownPlaceholders(shadowRoot) {
        if (!shadowRoot.querySelectorAll) return;
        const containers = shadowRoot.querySelectorAll('[class*="_selectContainer_"]');
        containers.forEach((container) => {
            if (container.dataset.akinDropdown === 'set') return;
            const label = container.querySelector('p[class*="_label_"]');
            const toggleText = container.querySelector('[class*="_selectToggleText_"]');
            if (!label || !toggleText) return;
            const text = label.textContent.trim();
            if (!text) return;
            toggleText.setAttribute('data-placeholder', text);
            container.dataset.akinDropdown = 'set';
        });
    }

    function scan(root) {
        if (!root || !root.querySelectorAll) return;
        root.querySelectorAll('*').forEach((el) => {
            const tag = el.tagName && el.tagName.toLowerCase();
            if (!tag) return;
            if (el.shadowRoot && (tag.startsWith('shopify-') || tag.includes('form'))) {
                inject(el.shadowRoot);
            }
        });
    }

    function init() {
        scan(document);
        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                m.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;
                    if (node.shadowRoot) inject(node.shadowRoot);
                    scan(node);
                });
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();

/* ============================================================
 * Login redirect interceptor
 * Rewrites any <a href="..."> pointing to Shopify customer login or
 * register pages to include `?return_to=/`, so customers land on the
 * home page after sign-in. Catches links rendered by the theme,
 * inline scripts, and same-origin links inside open shadow DOMs.
 * ============================================================ */
(function () {
    // Flip to false to disable the redirect-to-home-after-login behavior.
    const ENABLED = true;
    if (!ENABLED) return;

    const RETURN_TO = '/';
    const LOGIN_PATH_RE = /\/(customer_authentication|account)\/(login|register)\b/;

    function rewriteHref(link) {
        if (!link || typeof link.getAttribute !== 'function') return;
        const href = link.getAttribute('href');
        if (!href || !LOGIN_PATH_RE.test(href)) return;
        if (/[?&]return_to=/.test(href)) return;
        try {
            const url = new URL(href, window.location.origin);
            url.searchParams.set('return_to', RETURN_TO);
            link.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (err) {
            /* ignore */
        }
    }

    function scanLinks(root) {
        if (!root || typeof root.querySelectorAll !== 'function') return;
        root.querySelectorAll('a[href]').forEach(rewriteHref);
    }

    function init() {
        scanLinks(document);
        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                m.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;
                    if (node.tagName === 'A') rewriteHref(node);
                    scanLinks(node);
                });
                if (m.type === 'attributes' && m.target && m.target.tagName === 'A') {
                    rewriteHref(m.target);
                }
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['href'],
        });
    }

    // Click capture as a fallback for links that originate inside an
    // open shadow DOM (composedPath surfaces them).
    document.addEventListener(
        'click',
        function (e) {
            const path = e.composedPath ? e.composedPath() : [e.target];
            for (const node of path) {
                if (node && node.tagName === 'A') {
                    rewriteHref(node);
                    break;
                }
            }
        },
        true
    );

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
